import React, { useState } from 'react';
import { UserPlus, Edit2 } from 'lucide-react';
import { Tournament } from '../../types';

interface RegistrationSectionProps {
  tournament: Tournament;
  isAdmin: boolean;
  onTournamentUpdate: (tournament: Tournament) => void;
  onStartRegistration: (tournamentId: string) => void;
}

export function RegistrationSection({
  tournament,
  isAdmin,
  onTournamentUpdate,
  onStartRegistration
}: RegistrationSectionProps) {
  const [isEditingText, setIsEditingText] = useState(false);
  const [registrationClosed, setRegistrationClosed] = useState(() => {
    const now = new Date();
    const deadline = tournament.registrationDeadline ? 
      (() => {
        const d = new Date(tournament.registrationDeadline);
        d.setUTCHours(23, 59, 59, 999);
        return d;
      })() :
      new Date(tournament.startDate);
    return now > deadline;
  });
  const [registrationText, setRegistrationText] = useState(() => ({
    title: tournament?.metadata?.registrationText?.title || 'Tournament Registration Open',
    message: tournament?.metadata?.registrationText?.message || '$20 Late registration fee applies after deadline.\nNo refunds. \nCredit is available up to 72 hours before tournament.\nContact Jimmy Nguyen for any questions.',
  }));
  const [tempRegistrationText, setTempRegistrationText] = useState(registrationText);

  // Update registration status when tournament changes
  React.useEffect(() => {
    const now = new Date();
    const deadline = tournament.registrationDeadline ? 
      (() => {
        const d = new Date(tournament.registrationDeadline);
        d.setUTCHours(23, 59, 59, 999);
        return d;
      })() :
      new Date(tournament.startDate);
    setRegistrationClosed(now > deadline);
  }, [tournament.registrationDeadline, tournament.startDate]);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="text-center py-12">
        <UserPlus className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <div className="text-center relative">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {registrationClosed ? 'Registration Closed' : registrationText.title}
          </h3>
          {/* Format date with local timezone */}
          <p className="text-gray-600 max-w-sm mx-auto mb-6 whitespace-pre-line">
            {registrationClosed 
              ? `Registration period has ended on ${new Date((tournament.registrationDeadline || tournament.startDate) + 'T23:59:59.999Z').toLocaleDateString()}`
              : registrationText.message
            }
          </p>
          {isAdmin && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsEditingText(true);
              }}
              className="absolute top-0 right-0 p-2 text-gray-400 hover:text-gray-600"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!registrationClosed) {
                onStartRegistration(tournament.id);
              }
            }}
            disabled={registrationClosed}
            className={`px-4 py-2 text-white rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center justify-center gap-2 mx-auto ${
              registrationClosed
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary-600 hover:bg-primary-700'
            }`}
          >
            <UserPlus className="w-4 h-4" />
            {registrationClosed ? 'Registration Closed' : 'Register for Tournament'}
          </button>
          {!registrationClosed && (
            <p className="mt-2 text-xs text-gray-500">
              Registration closes on {(() => {
                const d = new Date(tournament.registrationDeadline || tournament.startDate);
                d.setUTCHours(23, 59, 59, 999);
                return d.toLocaleDateString();
              })()}
            </p>
          )}
        </div>
      </div>

      {/* Text Editing Modal */}
      {isEditingText && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Edit Registration Text</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={tempRegistrationText.title}
                  onChange={(e) => setTempRegistrationText(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  value={tempRegistrationText.message}
                  onChange={(e) => setTempRegistrationText(prev => ({ ...prev, message: e.target.value }))}
                  rows={5}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Enter registration message (use line breaks for formatting)"
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setIsEditingText(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setRegistrationText(tempRegistrationText);
                    setIsEditingText(false);
                    onTournamentUpdate({
                      ...tournament,
                      metadata: {
                        ...tournament.metadata,
                        registrationText: tempRegistrationText
                      }
                    });
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}